import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { SupplierProduct } from '../../lib/types';
import {
  Package,
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Tag,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye
} from 'lucide-react';

interface SupplierProductViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: SupplierProduct;
}

export default function SupplierProductViewModal({
  isOpen,
  onClose,
  product
}: SupplierProductViewModalProps) {
  // Don't render if no product data
  if (!product) {
    return null;
  }

  const getStockStatus = (inventory: any) => {
    if (!inventory) {
      return { label: 'Unknown', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle };
    }
    if (inventory.isOutOfStock) {
      return { label: 'Out of Stock', color: 'bg-red-100 text-red-800', icon: XCircle };
    }
    if (inventory.isLowStock) {
      return { label: 'Low Stock', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle };
    }
    return { label: 'In Stock', color: 'bg-green-100 text-green-800', icon: CheckCircle2 };
  };

  const stockStatus = getStockStatus(product?.inventory);
  const StatusIcon = stockStatus.icon;

  return (
    <EnhancedDialog open={isOpen} onOpenChange={onClose}>
      <EnhancedDialogContent size="4xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {product?.product?.name || 'Product Details'}
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Complete product and supplier information
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <EnhancedDialogBody>
          <div className="space-y-6">
            {/* Product Images & Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Image Gallery */}
              <div className="md:col-span-1 space-y-4">
                <div className="aspect-square rounded-lg overflow-hidden border bg-muted">
                  {product?.product?.images?.length > 0 ? (
                    <img
                      src={product.product.images.find(img => img.isDefault)?.url || product.product.images[0]?.url}
                      alt={product?.product?.name || 'Product'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Package className="h-12 w-12" />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {(product?.product?.images || []).map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-md overflow-hidden border bg-muted">
                      <img
                        src={img.url}
                        alt={`View ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Details */}
              <div className="md:col-span-2 space-y-6">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-2xl font-bold">{product?.product?.name || 'N/A'}</h3>
                      <p className="text-muted-foreground mt-1">{product?.product?.category?.name || 'Uncategorized'}</p>
                    </div>
                    <Badge className={stockStatus.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {stockStatus.label}
                    </Badge>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                    {product?.product?.description || 'No description available'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-sm font-medium">Pricing</span>
                      </div>
                      <div className="text-2xl font-bold">₹{(product.price || 0).toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground mt-1">per {product?.product?.unit || 'unit'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <Package className="h-4 w-4" />
                        <span className="text-sm font-medium">Inventory</span>
                      </div>
                      <div className="text-2xl font-bold">{product?.inventory?.availableStock ?? 0}</div>
                      <p className="text-xs text-muted-foreground mt-1">{product?.product?.unit || 'unit'}s available</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Min Order Qty</span>
                    <span className="font-medium">{product?.minOrderQuantity ?? 1} {product?.product?.unit || 'unit'}s</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Max Order Qty</span>
                    <span className="font-medium">{product?.maxQuantity ?? 0} {product?.product?.unit || 'unit'}s</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">HSN Code</span>
                    <span className="font-medium">{product?.product?.hsnCode || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">GST Rate</span>
                    <span className="font-medium">{product?.product?.gstRate ?? 18}%</span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Supplier Details */}
            <div>
              <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Supplier Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Business Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{product?.supplier?.businessName || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground">GST: {product?.supplier?.gstNumber || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 mt-4">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                      <p className="text-sm text-muted-foreground">
                        {product.supplier?.address?.street || 'N/A'}, {product.supplier?.address?.city || 'N/A'},<br />
                        {product.supplier?.address?.state || 'N/A'} - {product.supplier?.address?.pincode || 'N/A'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{product?.supplier?.ownerName || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground">Primary Contact</p>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{product?.supplier?.mobile || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{product?.supplier?.email || 'N/A'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <Separator />

            {/* Product Specifications */}
            {product?.product?.specifications && product.product.specifications.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Specifications
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {product.product.specifications.map((spec, index) => (
                    <div key={index} className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{spec.name}</p>
                      <p className="font-medium">{spec.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Created: {new Date(product.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>Last Updated: {new Date(product.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </EnhancedDialogBody>

        <EnhancedDialogFooter>
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        </EnhancedDialogFooter>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}